import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FaFileInvoiceDollar } from "react-icons/fa";
import { FiArrowLeft, FiSave, FiCheckCircle } from "react-icons/fi";
import Swal from "sweetalert2";

const InvoiceFormPO = () => {
  const { poNumber } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");

  const [poData, setPoData] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [amount, setAmount] = useState(0);
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [paidDate, setPaidDate] = useState(new Date().toISOString().split("T")[0]);
  const [previousInvoices, setPreviousInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  useEffect(() => {
    const fetchPOData = async () => {
      if (!poNumber) return;
      try {
        const encodedPoNumber = encodeURIComponent(poNumber);
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/v1/purchasing-order/${encodedPoNumber}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);

        const result = await response.json();
        setPoData(result.data);
        if (result.data) {
          setAmount(result.data.total_amount || 0);
        }
      } catch (error) {
        console.error("Error fetching PO data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPOData();
  }, [poNumber, token]);

  useEffect(() => {
    const fetchPreviousInvoices = async () => {
      if (!poData?.id) return;
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/v1/invoice?po_id=${poData.id}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const result = await response.json();
        setPreviousInvoices(result.data || []);
      } catch (error) {
        console.error("Error fetching previous invoices:", error);
      }
    };

    fetchPreviousInvoices();
  }, [poData, token]);

  const handleSave = async () => {
    if (!invoiceNumber || !dueDate) {
      Swal.fire({
        icon: "warning",
        title: "Form Belum Lengkap",
        text: "Silakan isi Nomor Invoice dan Tanggal Jatuh Tempo!",
        customClass: { popup: "rounded-3xl" },
      });
      return;
    }

    setLoadingSubmit(true);
    try {
      const payload = {
        id_po: poData?.id || 0,
        number: invoiceNumber,
        amount: Number(amount),
        description:
          description || `Pembayaran ${poData?.po_number || poData?.number} #${previousInvoices.length + 1}`,
        due_date: dueDate,
        paid_date: paidDate,
      };

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/v1/invoice`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit invoice. Status: ${response.status}`);
      }

      await Swal.fire({
        title: "Invoice Berhasil Disimpan!",
        text: `Invoice untuk PO ${poNumber} telah dibuat.`,
        icon: "success",
        timer: 1800,
        showConfirmButton: false,
        customClass: { popup: "rounded-3xl" },
      });

      navigate("/inventory/invoice");
    } catch (error) {
      console.error("Error saving invoice:", error);
      Swal.fire({
        title: "Gagal Menyimpan",
        text: "Terjadi kesalahan saat menyimpan invoice.",
        icon: "error",
        customClass: { popup: "rounded-3xl" },
      });
    } finally {
      setLoadingSubmit(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20 min-h-screen">
        <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const number = poData?.po_number || poData?.number || poNumber;
  const status = poData?.status || "Approved";
  const total_amount = poData?.total_amount || 0;
  const previousTotal = previousInvoices.reduce((acc, inv) => acc + (inv.amount || 0), 0);
  const remainingAmount = Math.max(total_amount - previousTotal, 0);

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/inventory/invoice")}
            className="p-2.5 rounded-2xl bg-white hover:bg-gray-100 border border-gray-200 text-gray-700 transition-colors shadow-sm"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              Buat Invoice PO: {number}
            </h1>
            <p className="text-xs text-gray-500">
              Buat tagihan pembayaran langsung dari Purchase Order ini
            </p>
          </div>
        </div>
      </motion.div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Form */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="lg:col-span-2 bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 space-y-5"
        >
          <h2 className="text-base font-extrabold text-gray-900 border-b border-gray-100 pb-3 flex items-center gap-2">
            <FaFileInvoiceDollar className="text-emerald-600 w-5 h-5" />
            <span>Form Tagihan Invoice PO</span>
          </h2>

          <div className="space-y-4 text-xs">
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Nomor Invoice Penagihan
              </label>
              <input
                type="text"
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="Contoh: INV/2026/08/001"
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-mono font-bold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Nominal Tagihan (Rp)
              </label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(Number(e.target.value))}
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-mono font-bold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Tanggal Jatuh Tempo
                </label>
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Tanggal Dibayar
                </label>
                <input
                  type="date"
                  value={paidDate}
                  onChange={(e) => setPaidDate(e.target.value)}
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-bold text-gray-700 uppercase tracking-wider">
                Catatan / Deskripsi
              </label>
              <textarea
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Catatan tagihan..."
                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-2xl text-xs text-gray-800 font-semibold focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
              />
            </div>
          </div>

          <div className="pt-4 border-t border-gray-100 flex justify-end gap-3">
            <button
              onClick={() => navigate("/inventory/invoice")}
              className="px-4 py-2.5 rounded-2xl bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold transition-colors"
            >
              Batal
            </button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleSave}
              disabled={loadingSubmit}
              className="px-6 py-2.5 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold shadow-lg shadow-emerald-600/20 transition-all flex items-center gap-2"
            >
              <FiSave className="w-4 h-4" />
              <span>{loadingSubmit ? "Menyimpan..." : "Simpan Invoice"}</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Right Column: PO Info Summary */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="bg-white rounded-3xl p-6 shadow-xl shadow-gray-200/50 border border-gray-100 space-y-4"
        >
          <h2 className="text-base font-extrabold text-gray-900 border-b border-gray-100 pb-3">
            Ringkasan PO #{number}
          </h2>

          <div className="space-y-3 text-xs">
            <div className="flex justify-between items-center text-gray-600">
              <span>Distributor PBF:</span>
              <span className="font-bold text-gray-800">{poData?.distributor?.name || "-"}</span>
            </div>
            <div className="flex justify-between items-center text-gray-600">
              <span>Outlet Penempatan:</span>
              <span className="font-bold text-gray-800">{poData?.outlet?.name || "-"}</span>
            </div>
            <div className="flex justify-between items-center text-gray-600">
              <span>Status PO:</span>
              <span className="font-bold text-teal-700 capitalize">{status}</span>
            </div>

            <div className="p-3.5 rounded-2xl bg-emerald-50 border border-emerald-100 space-y-1.5 mt-4">
              <div className="flex justify-between text-gray-600">
                <span>Total PO:</span>
                <span className="font-extrabold text-emerald-800 font-mono">
                  Rp {total_amount.toLocaleString("id-ID")}
                </span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Telah Dibayar (Invoice Lalu):</span>
                <span className="font-bold text-teal-700 font-mono">
                  Rp {previousTotal.toLocaleString("id-ID")}
                </span>
              </div>
              <div className="flex justify-between text-gray-900 font-bold border-t border-emerald-200/60 pt-1.5">
                <span>Sisa Tagihan PO:</span>
                <span className="font-extrabold text-emerald-900 font-mono">
                  Rp {remainingAmount.toLocaleString("id-ID")}
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default InvoiceFormPO;
